import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Avatar,
    Chip,
    Divider,
    Fade,
    Zoom,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { 
    LinkedIn,
    CheckCircle,
    Cancel,
    Refresh,
    OpenInNew,
    BusinessCenter,
    LocationOn,
    Email,
} from "@mui/icons-material";
import safeToast from "utils/safeToast";
import { API_ENDPOINTS } from "config/api";

// Resolve LinkedIn values from either Vite or CRA env styles
const isViteEnv = typeof import.meta !== "undefined" && import.meta.env;
const getEnvValue = (key) => {
    if (isViteEnv) {
        return (
            import.meta.env[key] ||
            import.meta.env[`VITE_${key.replace("REACT_APP_", "").replace("VITE_", "")}`] ||
            ""
        );
    }

    if (typeof process !== "undefined" && process.env) {
        return (
            process.env[key] ||
            process.env[`REACT_APP_${key.replace("REACT_APP_", "").replace("VITE_", "")}`] ||
            process.env[`VITE_${key.replace("REACT_APP_", "").replace("VITE_", "")}`] ||
            ""
        );
    }

    return "";
};

const LinkedInLoginButton = ({ clientId, redirectUri }) => {
    const handleLogin = () => {
        // This URL should match the one in your LinkedIn app configuration
        const scope = "r_liteprofile r_emailaddress";
        const state = `alumnus_state_${Date.now()}`; // random string for CSRF protection
        const encodedRedirectUri = encodeURIComponent(redirectUri);
        
        const linkedInAuthUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${clientId}&redirect_uri=${encodedRedirectUri}&scope=${scope}&state=${state}`;
        
        // Store state in localStorage for verification
        localStorage.setItem("linkedin_oauth_state", state);
        
        window.location.href = linkedInAuthUrl;
    };

    return (
        <MDButton
            onClick={handleLogin}
            variant="gradient"
            color="info"
            startIcon={<LinkedIn sx={{ fontSize: 24 }} />}
            sx={{
                px: 3,
                py: 1.5,
                borderRadius: 3,
                textTransform: "none",
                fontSize: "0.9375rem",
                fontWeight: 600,
                boxShadow: "0 4px 14px 0 rgba(0, 118, 181, 0.39)",
                transition: "all 0.3s ease",
                "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px 0 rgba(0, 118, 181, 0.5)",
                },
            }}
        >
            Connect with LinkedIn
        </MDButton>
    );
};

LinkedInLoginButton.propTypes = {
    clientId: PropTypes.string.isRequired,
    redirectUri: PropTypes.string.isRequired,
};

const LinkedInCallback = ({ onComplete }) => {
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState(null);
    const [error, setError] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get("code");
        const state = urlParams.get("state");
        const storedState = localStorage.getItem("linkedin_oauth_state");

        if (!code) {
            setError("No authorization code received from LinkedIn.");
            setLoading(false);
            return;
        }

        // Verify state to prevent CSRF attacks
        if (state !== storedState) {
            setError("State mismatch. Please try again.");
            setLoading(false);
            return;
        }

        // Clear the stored state
        localStorage.removeItem("linkedin_oauth_state");

        // Call your backend callback endpoint
        // Note: Your backend should handle the OAuth token exchange
        axios
            .get(API_ENDPOINTS.LINKEDIN.CALLBACK(code, state))
            .then((res) => {
                setProfile(res.data);
                localStorage.setItem("linkedinProfile", JSON.stringify(res.data));
                safeToast.success("LinkedIn profile connected successfully!");
                setLoading(false);
                
                // Redirect back to MyDetails page after 2 seconds
                setTimeout(() => {
                    navigate("/MyDetails");
                }, 2000);
                
                // Notify parent component if callback provided
                if (onComplete) {
                    onComplete(res.data);
                }
            })
            .catch((err) => {
                console.error("LinkedIn Callback Error:", err);
                setError(err.response?.data?.message || "Failed to connect LinkedIn profile.");
                safeToast.error("Failed to connect LinkedIn profile.");
                setLoading(false);
            });
    }, [location]);

    if (loading) {
        return (
            <MDBox 
                display="flex" 
                flexDirection="column"
                justifyContent="center" 
                alignItems="center" 
                py={6}
            >
                <CircularProgress 
                    size={60}
                    sx={{ 
                        color: "#0077B5",
                        mb: 3,
                    }} 
                />
                <MDTypography variant="body1" fontWeight="medium" mb={1} sx={{ fontSize: "0.9375rem" }}>
                    Connecting to LinkedIn...
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                    Please wait while we fetch your profile
                </MDTypography>
            </MDBox>
        );
    }

    if (error) {
        return (
            <MDBox textAlign="center" py={4}>
                <Zoom in={true}>
                    <MDBox
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            backgroundColor: "error.light",
                            mb: 3,
                        }}
                    >
                        <Cancel sx={{ fontSize: 48, color: "error.main" }} />
                    </MDBox>
                </Zoom>
                <MDTypography variant="body1" color="error" mb={1} fontWeight="bold" sx={{ fontSize: "0.9375rem" }}>
                    Connection Failed
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mb={3} sx={{ fontSize: "0.8125rem" }}>
                    {error}
                </MDTypography>
                <MDButton
                    variant="outlined"
                    color="error"
                    startIcon={<Refresh />}
                    onClick={() => window.location.reload()}
                >
                    Try Again
                </MDButton>
            </MDBox>
        );
    }

    return (
        <MDBox textAlign="center" py={4}>
            {profile ? (
                <Fade in={true}>
                    <MDBox>
                        <Zoom in={true}>
                            <Avatar
                                src={profile.profilePictureUrl}
                                alt="Profile"
                                sx={{
                                    width: 120,
                                    height: 120,
                                    mx: "auto",
                                    mb: 3,
                                    border: "4px solid #0077B5",
                                    boxShadow: "0 4px 20px rgba(0, 118, 181, 0.3)",
                                }}
                            />
                        </Zoom>
                        <MDTypography variant="h6" fontWeight="bold" mb={1} sx={{ fontSize: "1.25rem" }}>
                            Welcome {profile.firstName}!
                        </MDTypography>
                        {profile.headline && (
                            <MDTypography variant="body2" color="text.secondary" mb={3} sx={{ fontSize: "0.875rem" }}>
                                {profile.headline}
                            </MDTypography>
                        )}
                        <Chip
                            icon={<CheckCircle />}
                            label="Successfully Connected"
                            color="success"
                            sx={{ mt: 2 }}
                        />
                    </MDBox>
                </Fade>
            ) : (
                <MDTypography variant="body2" color="text.secondary">
                    No profile data found.
                </MDTypography>
            )}
        </MDBox>
    );
};

LinkedInCallback.propTypes = {
    onComplete: PropTypes.func,
};

const ProfileDisplay = () => {
    const storedProfile = localStorage.getItem("linkedinProfile");
    const profile = storedProfile ? JSON.parse(storedProfile) : null;

    if (!profile) {
        return (
            <MDBox textAlign="center" py={4}>
                <MDTypography variant="body1" color="text.secondary">
                    No LinkedIn profile connected.
                </MDTypography>
            </MDBox>
        );
    }

    return (
        <Card 
            sx={{ 
                boxShadow: 6, 
                borderRadius: 3, 
                maxWidth: 600, 
                mx: "auto",
                overflow: "hidden",
                transition: "all 0.3s ease",
                "&:hover": {
                    boxShadow: 8,
                },
            }}
        >
            <MDBox
                sx={{
                    background: "linear-gradient(135deg, #0077B5 0%, #004182 100%)",
                    py: 3,
                    textAlign: "center",
                }}
            >
                <Zoom in={true}>
                    <Avatar
                        src={profile.profilePictureUrl}
                        alt="Profile"
                        sx={{
                            width: 140,
                            height: 140,
                            mx: "auto",
                            mb: 2,
                            border: "5px solid white",
                            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
                        }}
                    />
                </Zoom>
                <MDTypography variant="h6" fontWeight="bold" color="white" mb={1} sx={{ fontSize: "1.25rem" }}>
                    {profile.firstName} {profile.lastName}
                </MDTypography>
                {profile.headline && (
                    <MDTypography variant="body2" color="white" sx={{ opacity: 0.9, fontSize: "0.875rem" }}>
                        {profile.headline}
                    </MDTypography>
                )}
            </MDBox>
            <CardContent sx={{ py: 3 }}>
                <MDBox>
                    {profile.location && (
                        <MDBox display="flex" alignItems="center" mb={1.5} justifyContent="center">
                            <LocationOn sx={{ color: "text.secondary", mr: 1, fontSize: "1rem" }} />
                            <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                                {profile.location}
                            </MDTypography>
                        </MDBox>
                    )}
                    {profile.company && (
                        <MDBox display="flex" alignItems="center" mb={1.5} justifyContent="center">
                            <BusinessCenter sx={{ color: "text.secondary", mr: 1, fontSize: "1rem" }} />
                            <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                                {profile.company}
                            </MDTypography>
                        </MDBox>
                    )}
                    {profile.email && (
                        <MDBox display="flex" alignItems="center" mb={2} justifyContent="center">
                            <Email sx={{ color: "text.secondary", mr: 1, fontSize: "1rem" }} />
                            <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                                {profile.email}
                            </MDTypography>
                        </MDBox>
                    )}
                </MDBox>
                <Divider sx={{ my: 2 }} />
                {profile.profileUrl && (
                    <MDButton
                        href={profile.profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="gradient"
                        color="info"
                        startIcon={<OpenInNew />}
                        fullWidth
                        sx={{
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                        }}
                    >
                        View Full Profile on LinkedIn
                    </MDButton>
                )}
            </CardContent>
        </Card>
    );
};

const LinkedInIntegration = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const clientId = useMemo(() => {
        const envClientId = getEnvValue("REACT_APP_LINKEDIN_CLIENT_ID");
        return envClientId || "78lgooexgusbds";
    }, []);
    // Frontend callback route - backend will handle the actual OAuth exchange
    const redirectUri = useMemo(() => {
        const envRedirect = getEnvValue("REACT_APP_LINKEDIN_REDIRECT_URI");
        if (envRedirect) {
            return envRedirect;
        }
        if (typeof window === "undefined") {
            return "";
        }
        return `${window.location.origin}/MyDetails`;
    }, []);
    const [hasProfile, setHasProfile] = useState(false);

    useEffect(() => {
        // Check if we have a LinkedIn profile stored
        const storedProfile = localStorage.getItem("linkedinProfile");
        setHasProfile(!!storedProfile);

        // Check if we're coming from LinkedIn callback
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get("code");
        
        if (code) {
            // We're in callback mode, LinkedInCallback component will handle it
            return;
        }
    }, [location]);

    // If we're in callback mode (has code param), show callback component
    const urlParams = new URLSearchParams(location.search);
    const isCallback = urlParams.get("code");

    if (isCallback) {
        return (
            <MDBox px={2} py={3}>
                <LinkedInCallback 
                    onComplete={(profileData) => {
                        setHasProfile(true);
                    }}
                />
            </MDBox>
        );
    }

    return (
        <MDBox px={2} py={3}>
            <Card 
                sx={{ 
                    boxShadow: 6, 
                    borderRadius: 3,
                    overflow: "visible",
                    background: "linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)",
                }}
            >
                <MDBox
                    sx={{
                        background: "linear-gradient(135deg, #0077B5 0%, #004182 100%)",
                        color: "white",
                        px: 3,
                        py: 4,
                        textAlign: "center",
                        position: "relative",
                        overflow: "visible",
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: "url('data:image/svg+xml;utf8,<svg width=\"100\" height=\"100\" xmlns=\"http://www.w3.org/2000/svg\"><defs><pattern id=\"grid\" width=\"20\" height=\"20\" patternUnits=\"userSpaceOnUse\"><path d=\"M 20 0 L 0 0 0 20\" fill=\"none\" stroke=\"rgba(255,255,255,0.1)\" stroke-width=\"1\"/></pattern></defs><rect width=\"100\" height=\"100\" fill=\"url(%23grid)\" /></svg>')",
                            opacity: 0.3,
                        },
                    }}
                >
                    <Zoom in={true}>
                        <MDBox position="relative" zIndex={1}>
                            <MDBox
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 80,
                                    height: 80,
                                    borderRadius: "50%",
                                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                                    mb: 2,
                                    backdropFilter: "blur(10px)",
                                }}
                            >
                                <LinkedIn sx={{ fontSize: 48 }} />
                            </MDBox>
                            <MDTypography 
                                variant="h5" 
                                fontWeight="bold" 
                                color="white" 
                                mb={1.5} 
                                sx={{ fontSize: "1.5rem", wordWrap: "break-word" }}
                            >
                                LinkedIn Integration
                            </MDTypography>
                            <MDTypography 
                                variant="body2" 
                                color="white" 
                                sx={{ 
                                    opacity: 0.95,
                                    fontSize: "0.875rem",
                                    lineHeight: 1.5,
                                    wordWrap: "break-word",
                                    overflowWrap: "break-word",
                                    px: 1,
                                }}
                            >
                                Connect your LinkedIn profile<br />
                                to enhance your alumni network
                            </MDTypography>
                        </MDBox>
                    </Zoom>
                </MDBox>
                <CardContent sx={{ py: 4, px: 3, overflow: "visible" }}>
                    {hasProfile ? (
                        <Fade in={true}>
                            <MDBox>
                                <ProfileDisplay />
                                <MDBox mt={4} textAlign="center">
                                    <Chip
                                        icon={<CheckCircle />}
                                        label="Connected"
                                        color="success"
                                        sx={{ mb: 2 }}
                                    />
                                    <MDBox>
                                        <MDButton
                                            onClick={() => {
                                                localStorage.removeItem("linkedinProfile");
                                                setHasProfile(false);
                                                safeToast.info("LinkedIn profile disconnected.");
                                            }}
                                            variant="outlined"
                                            color="error"
                                            startIcon={<Cancel />}
                                            sx={{
                                                borderRadius: 2,
                                                textTransform: "none",
                                                px: 3,
                                            }}
                                        >
                                            Disconnect LinkedIn
                                        </MDButton>
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        </Fade>
                    ) : (
                        <MDBox textAlign="center">
                            <MDTypography variant="h6" fontWeight="medium" mb={2} color="text.primary" sx={{ fontSize: "1rem" }}>
                                Get Started
                            </MDTypography>
                            <MDTypography 
                                variant="body2" 
                                mb={4} 
                                color="text.secondary" 
                                sx={{ 
                                    maxWidth: 500, 
                                    mx: "auto", 
                                    fontSize: "0.875rem",
                                    lineHeight: 1.6,
                                    wordWrap: "break-word",
                                    overflowWrap: "break-word",
                                    px: 2,
                                }}
                            >
                                Connect your LinkedIn account to share your<br />
                                professional profile with fellow alumni<br />
                                and expand your professional network.
                            </MDTypography>
                            
                                <MDBox 
                                mb={3}
                                sx={{
                                    p: 2,
                                    backgroundColor: "#f8f9fa",
                                    borderRadius: 2,
                                    border: "1px solid #e9ecef",
                                }}
                            >
                                <MDBox display="flex" alignItems="center" justifyContent="center" mb={2} flexWrap="wrap" gap={1}>
                                    <Chip 
                                        icon={<BusinessCenter sx={{ fontSize: "0.875rem" }} />} 
                                        label="Share Professional Info" 
                                        size="small"
                                        sx={{ mr: 1, fontSize: "0.75rem", height: "24px" }}
                                    />
                                    <Chip 
                                        icon={<LocationOn sx={{ fontSize: "0.875rem" }} />} 
                                        label="Expand Network" 
                                        size="small"
                                        sx={{ mr: 1, fontSize: "0.75rem", height: "24px" }}
                                    />
                                    <Chip 
                                        icon={<Email sx={{ fontSize: "0.875rem" }} />} 
                                        label="Stay Connected" 
                                        size="small"
                                        sx={{ fontSize: "0.75rem", height: "24px" }}
                                    />
                                </MDBox>
                            </MDBox>

                            <LinkedInLoginButton clientId={clientId} redirectUri={redirectUri} />
                            
                            <MDBox mt={3}>
                                <MDTypography variant="caption" color="text.secondary" display="block" mb={1} sx={{ fontSize: "0.75rem" }}>
                                    <strong>🔒 Secure Connection</strong>
                                </MDTypography>
                                <MDTypography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                                    You&apos;ll be redirected to LinkedIn<br />
                                    to securely authorize the connection.<br />
                                    We only access your public profile information.
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                    )}
                </CardContent>
            </Card>
        </MDBox>
    );
};

export default LinkedInIntegration;

